/**
 * Decides where `/api/*` calls go at runtime.
 *
 * Topology is now **single-shape**: the SPA is always loaded from a web
 * hub (Cloudflare Pages, `127.0.0.1:10986` in dev, or any other static
 * host) and talks to the local adh agent at `http://127.0.0.1:10987`
 * via cross-origin fetch. The previous `monolithic` (same-origin) /
 * `local-shell` branch is gone — see .cursor/rules/spa-and-adh-split.mdc
 * for the rationale.
 *
 * Override via `window.__ADH_API_ORIGIN__` (string, absolute URL with
 * no trailing slash). Useful for:
 *   - QA pointing the SPA at a different agent port
 *   - LAN sharing demos (`http://192.168.x.x:10987`)
 *   - integration tests injecting a mock origin
 *
 * Why a constant-at-module-load instead of a function call per request?
 * The override doesn't change during a single SPA session (it's set
 * before the bundle evaluates), so reading once and freezing the result
 * keeps every downstream `fetch()` cheap and predictable.
 */

declare global {
  interface Window {
    __ADH_API_ORIGIN__?: string;
  }
}

const DEFAULT_API_ORIGIN = 'http://127.0.0.1:10987';

function resolveApiOrigin(): string {
  if (typeof window !== 'undefined' && typeof window.__ADH_API_ORIGIN__ === 'string') {
    const trimmed = window.__ADH_API_ORIGIN__.replace(/\/+$/, '');
    if (trimmed.length > 0) return trimmed;
  }
  return DEFAULT_API_ORIGIN;
}

/**
 * Always `false` — kept as an export so legacy call sites that branch
 * on this constant compile without changes. They fall into the
 * cross-origin path, which is the only path now.
 */
export const IS_SAME_ORIGIN: boolean = false;

/**
 * Always `true` — opposite of {@link IS_SAME_ORIGIN}, used by some
 * onboarding screens that adjust copy based on "did the SPA come from
 * a public domain". In the new topology this is always true; legacy
 * checks remain valid.
 */
export const IS_PUBLIC_HUB: boolean = true;

/**
 * Absolute origin of the adh agent. Every `/api/*` URL is prefixed with
 * this. Override via `window.__ADH_API_ORIGIN__`.
 */
export const API_BASE: string = resolveApiOrigin();

/**
 * `credentials` value to pass to `fetch()`. Forced to `'omit'` because
 * the open-to-all CORS policy on the agent can't legally combine
 * `Allow-Origin: *` with `Allow-Credentials: true`.
 */
export const API_CREDENTIALS: RequestCredentials = 'omit';

/**
 * Concatenate {@link API_BASE} with a path-or-URL safely.
 *
 * - Absolute URLs (`http://…`, `https://…`, `ws://…`, `wss://…`,
 *   protocol-relative `//…`) are returned unchanged so callers that
 *   already constructed a full URL (e.g. websocket builders) don't
 *   get double-prefixed.
 * - Relative paths get prepended with `API_BASE`, ensuring the path
 *   begins with `/`.
 */
export function withApiBase(path: string): string {
  if (/^([a-z][a-z0-9+.-]*:)?\/\//i.test(path)) return path;
  if (path.startsWith('/')) return API_BASE + path;
  return `${API_BASE}/${path}`;
}
